/**
 * Formatting helpers. Date/duration/pace/distance formatting is owned by @gd/shared/dates;
 * this module re-exports it and adds browser-facing helpers (relative time, unit preference)
 * that don't belong in the shared package.
 */
import {
  formatDuration,
  formatGap,
  formatHours,
  formatSpan,
  formatPace,
  formatSwimPace,
  formatDistance,
  parseIsoDate,
  daysBetween,
} from '@gd/shared/dates'

export {
  formatDuration,
  formatGap,
  formatHours,
  formatSpan,
  formatPace,
  formatSwimPace,
  formatDistance,
}

/** Tabular-friendly number formatting: 0-1 decimals, thousands separators. */
export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '–'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['week', 60 * 60 * 24 * 7],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
]

const relativeFormatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' })

/** "3 hours ago", "just now". `iso` is a full ISO datetime (with offset or Z). */
export function formatRelativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '–'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '–'
  const diffSecs = Math.round((then - now.getTime()) / 1000)
  const abs = Math.abs(diffSecs)
  if (abs < 45) return 'Just now'
  for (const [unit, secs] of RELATIVE_UNITS) {
    if (abs >= secs || unit === 'minute') {
      const value = Math.round(diffSecs / secs)
      return relativeFormatter.format(value, unit)
    }
  }
  return 'Just now'
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})
const dateFormatterNoWeekday = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' })
const dateFormatterWithYear = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

/** Format a YYYY-MM-DD local date as "Sat 5 Sep". */
export function formatDate(date: string, opts: { weekday?: boolean; year?: boolean } = {}): string {
  const d = parseIsoDate(date)
  const utcAsLocal = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  if (opts.year) return dateFormatterWithYear.format(utcAsLocal)
  if (opts.weekday === false) return dateFormatterNoWeekday.format(utcAsLocal)
  return dateFormatter.format(utcAsLocal)
}

/** Format a from/to YYYY-MM-DD pair as "5 Sep – 3 Dec 2026". */
export function formatDateRange(from: string, to: string): string {
  const sameYear = from.slice(0, 4) === to.slice(0, 4)
  const fromLabel = formatDate(from, { weekday: false })
  const toLabel = formatDate(to, { weekday: false, year: true })
  if (daysBetween(from, to) === 0) return formatDate(to, { weekday: false, year: true })
  return sameYear
    ? `${fromLabel} to ${toLabel}`
    : `${formatDate(from, { weekday: false, year: true })} to ${toLabel}`
}

/** A span on a page that already names its year: "Oct 19 to 25", "Sep 28 to Oct 4". */
export function formatShortDateRange(from: string, to: string): string {
  const fromLabel = formatDate(from, { weekday: false })
  if (from === to) return fromLabel
  const toLabel = formatDate(to, { weekday: false })
  return `${fromLabel} to ${from.slice(0, 7) === to.slice(0, 7) ? toLabel.split(' ')[1] : toLabel}`
}

// ───────────────────────────── Units ─────────────────────────────

export interface UnitPreferences {
  /** Distance display unit. Skyler is US-based: default miles. */
  distance: 'km' | 'mi'
  /** Swim pace display unit: per 100 metres or per 100 yards. */
  swimPace: 'm' | 'yd'
  /** Body weight display unit. */
  weight: 'kg' | 'lb'
}

const UNITS_KEY = 'td-units'

const DEFAULT_UNITS: UnitPreferences = { distance: 'mi', swimPace: 'm', weight: 'lb' }

export function readUnitPreferences(): UnitPreferences {
  if (typeof window === 'undefined') return DEFAULT_UNITS
  try {
    const raw = window.localStorage.getItem(UNITS_KEY)
    if (!raw) return DEFAULT_UNITS
    const parsed = JSON.parse(raw) as Partial<UnitPreferences>
    return { ...DEFAULT_UNITS, ...parsed }
  } catch {
    return DEFAULT_UNITS
  }
}

export function writeUnitPreferences(units: UnitPreferences): void {
  try {
    window.localStorage.setItem(UNITS_KEY, JSON.stringify(units))
  } catch {
    // ignore (private browsing, storage disabled)
  }
}

/** Swim pace per 100m or per 100yd, from a seconds-per-100m value. */
export function formatSwimPaceUnit(
  secPer100m: number | null | undefined,
  unit: 'm' | 'yd' = 'm',
): string {
  if (secPer100m == null || !Number.isFinite(secPer100m) || secPer100m <= 0) return '–'
  const secs = Math.round(unit === 'yd' ? secPer100m * 0.9144 : secPer100m)
  const m = Math.floor(secs / 60)
  const sec = secs % 60
  return `${m}:${String(sec).padStart(2, '0')} /100${unit}`
}

export const LB_PER_KG = 2.20462

/** A kilogram value converted to the display unit (null passes through). */
export function kgTo(kg: number | null, unit: 'kg' | 'lb'): number | null {
  return kg == null ? null : unit === 'lb' ? kg * LB_PER_KG : kg
}

const METRES_PER_YARD = 0.9144

/** A swim distance in metres or yards, whole numbers: "2,500 m". Swims never read in km or mi. */
export function formatSwimDistance(
  metres: number | null | undefined,
  unit: 'm' | 'yd' = 'm',
): string {
  if (metres == null || !Number.isFinite(metres)) return '–'
  const value = unit === 'yd' ? metres / METRES_PER_YARD : metres
  return `${formatNumber(Math.round(value))} ${unit}`
}

/** A session's distance in its sport's unit: swims in m or yd, everything else in km or mi. */
export function formatSportDistance(
  metres: number | null | undefined,
  sportGroup: string,
  units: Pick<UnitPreferences, 'distance' | 'swimPace'>,
): string {
  return sportGroup === 'swim'
    ? formatSwimDistance(metres, units.swimPace)
    : formatDistance(metres, units.distance)
}

/** Total weight lifted (kg) as a whole number in the display unit, e.g. "12,450 lb". */
export function formatLifted(kg: number, unit: 'kg' | 'lb'): string {
  return `${formatNumber(Math.round(kgTo(kg, unit) ?? 0))} ${unit}`
}

/** Body weight in kg, displayed in kg or lb. */
export function formatWeight(
  kg: number | null | undefined,
  unit: 'kg' | 'lb' = 'kg',
  digits = 1,
): string {
  if (kg == null || !Number.isFinite(kg)) return '–'
  const v = unit === 'lb' ? kg * LB_PER_KG : kg
  return `${v.toFixed(digits)} ${unit}`
}
